import React, { Fragment, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { Dialog, Transition } from "@headlessui/react";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import {
  baseURLImage,
  getNameProfile,
  selectedTabState,
} from "../../../Utils/utils";
import { useNavigate } from "react-router-dom";
import {
  PARTICIPANT_PROFILE_TABS,
  USER_ROLE,
} from "../../../Constants/constant";
import { apiGetRequest } from "../../../helpers/Requests";
import { useEffect } from "react";
import QuickSnapshots from "../../../components/common/QuickSnapshots/QuickSnapshots";
import moment from "moment";
import CustomButton from "../../../components/common/Button/CustomButton";
import { roleAccess } from "helpers/RolesPermission";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";

const QuickView = (props) => {
  const { selectedParticipant, isOpen, setIsOpen, paddingLeft } = props;

  const userRole = roleAccess([USER_ROLE.Sp_Standard_User]) ? "worker" : "sp";

  const navigate = useNavigate();
  const [caseNotes, setCaseNotes] = useState([]);
  const [settingData, setSettingData] = useState(null);
  const [request, setRequest] = React.useState({
    isRequested: false,
    isSuccess: false,
    isFailed: false,
  });

  const fetchParticipantList = async () => {
    setRequest({ ...request, isRequested: true });
    try {
      const res = await apiGetRequest(
        `/${userRole}/participants/quick-view/${selectedParticipant.uuid}`
      );
      if (res.status !== 200) {
        setRequest({ ...request, isRequested: false, isFailed: true });
        console.log("Something Went Wrong");
      } else if (res.status === 200) {
        setRequest({ ...request, isRequested: false, isSuccess: true });
        setCaseNotes(res.data.data.case_notes);
        setSettingData(res.data.data.participant_profile);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (Object.keys(selectedParticipant).length > 0) {
      fetchParticipantList();
    }
  }, [selectedParticipant]);

  const handleImageError = (e, fname, lname) => {
    return (e.target.src = getNameProfile(fname, lname));
  };

  const handleCaseNotes = () => {
    if (
      roleAccess(
        [],
        PERMISSION_KEYS.PARTICIPANT,
        PERMISSION_KEYS_CHILDREN.PARTICIPANT.DETAIL
      )
    ) {
      selectedTabState.save(JSON.stringify(PARTICIPANT_PROFILE_TABS[1]));
      const routeRole = userRole === "worker" ? "participant" : "sp";
      navigate(`/${routeRole}-profile/${selectedParticipant.uuid}`);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='fixed inset-0 z-50 overflow-hidden' onClose={setIsOpen}>
        <div className='flex min-h-screen items-center justify-end'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-blue-dark/30' />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300 transform'
            enterFrom='translate-x-full opacity-0'
            enterTo='translate-x-0 opacity-100'
            leave='ease-in duration-200 transform'
            leaveFrom='translate-x-0 opacity-100'
            leaveTo='translate-x-full opacity-0'
          >
            <div className='relative flex flex-col h-screen w-full max-w-lg bg-white shadow-xl border-l border-gray-200'>
              <div className='flex-shrink-0 px-6 py-4 border-b border-gray-200'>
                <div className='flex justify-between items-center'>
                  <span className='text-lg font-bold text-gray-900 capitalize'>Quick View</span>
                  <button 
                    onClick={setIsOpen}
                    className='p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors'
                    aria-label='Close'
                  >
                    <AiOutlineClose className='h-5 w-5' />
                  </button>
                </div>
              </div>
              
              <div className='flex-1 overflow-y-auto custom-scroll px-6 py-4'>
              {request.isRequested ? (
                <div className='flex justify-center items-center h-full min-h-[400px]'>
                  <CircularLoader
                    classes='flex justify-center items-center'
                    classloader='loader-l'
                  />
                </div>
              ) : (
                <>
                  <div className='mb-6 flex items-start space-x-4'>
                    <div className='h-16 w-16 rounded-full'>
                      <img
                        onError={(e) =>
                          handleImageError(
                            e,
                            selectedParticipant.first_name,
                            selectedParticipant.last_name
                          )
                        }
                        className='object-cover rounded-full border-2-white h-16 w-16'
                        src={
                          selectedParticipant.profile_picture
                            ? baseURLImage(selectedParticipant.profile_picture)
                            : getNameProfile(
                                selectedParticipant.first_name,
                                selectedParticipant.last_name || " "
                              )
                        }
                        alt=''
                      />
                    </div>
                    <div>
                      <p className='text-gray-900 text-lg font-medium capitalize'>
                        {`${selectedParticipant.first_name} ${selectedParticipant.last_name}`}
                      </p>
                      {roleAccess(
                        [],
                        PERMISSION_KEYS.PARTICIPANT,
                        PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_EMAIL
                      ) && (
                        <p className='text-gray-500'>
                          {selectedParticipant.email}
                        </p>
                      )}
                    </div>
                  </div>
                  {roleAccess(
                    [],
                    PERMISSION_KEYS.PARTICIPANT,
                    PERMISSION_KEYS_CHILDREN.PARTICIPANT.DETAIL
                  ) && (
                    <div className='mt-4 flex space-x-4'>
                      <CustomButton
                        variant='secondary'
                        clickHandler={handleCaseNotes}
                        label='Add Case Notes'
                      />
                      <CustomButton
                        clickHandler={() => {
                          if (
                            roleAccess(
                              [],
                              PERMISSION_KEYS.PARTICIPANT,
                              PERMISSION_KEYS_CHILDREN.PARTICIPANT.DETAIL
                            )
                          ) {
                            const routeRole =
                              userRole === "worker" ? "participant" : "sp";
                            navigate(
                              `/${routeRole}-profile/${selectedParticipant.uuid}`
                            );
                          }
                        }}
                        label='View profile'
                      />
                    </div>
                  )}
                  <div className='mt-6'>
                    <QuickSnapshots
                      isQuickViewCall={true}
                      cssClass='mt-0'
                      paddingLeft={paddingLeft}
                      participantsInfo={selectedParticipant}
                      settingsData={settingData}
                    />
                  </div>
                  <div className='mt-8'>
                    <h2 className='text-lg font-bold text-gray-900 mb-4'>
                      Case Notes
                    </h2>
                    {caseNotes && caseNotes.length > 0 ? (
                      <ul className='divide-y divide-gray-200 border-t border-b border-gray-200'>
                        {caseNotes.map((casenote, index) => (
                          <li key={index} className='py-4'>
                            <div className='flex items-start justify-between gap-4'>
                              <div className='flex-1 min-w-0'>
                                <p className='text-sm font-semibold text-gray-900 mb-1'>
                                  {casenote?.author?.name || 'Unknown'}
                                </p>
                                <div
                                  className='text-gray-600 text-sm mb-2 line-clamp-3'
                                  title={casenote.notes}
                                  dangerouslySetInnerHTML={{ __html: casenote.notes }}
                                />
                                <p className='text-xs text-gray-500'>
                                  {moment(casenote.created_at).format("DD/MM/YYYY")}
                                </p>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className='text-center py-8 border-t border-b border-gray-200'>
                        <p className='text-gray-500 text-sm'>No case notes available</p>
                      </div>
                    )}
                  </div>
                </>
              )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default QuickView;
